import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Search,
  Phone,
  Mail,
  User,
  Building2,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  RefreshCw,
  Calendar,
  Car,
  Euro,
  ArrowRight
} from "lucide-react";
import { format, parseISO, differenceInDays } from "date-fns";
import { fr } from "date-fns/locale";

const STATUS_CONFIG = {
  brouillon: { color: "bg-gray-500", label: "Brouillon", icon: FileText },
  envoye: { color: "bg-blue-500", label: "Envoyé", icon: Send },
  accepte: { color: "bg-green-500", label: "Accepté", icon: CheckCircle },
  refuse: { color: "bg-red-500", label: "Refusé", icon: XCircle },
  expire: { color: "bg-orange-500", label: "Expiré", icon: Clock }
};

export default function DevisPage() {
  const { user, agencies } = useAuth();
  const [devisList, setDevisList] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(user?.agency_id || "all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedDevis, setSelectedDevis] = useState(null);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [prestations, setPrestations] = useState([]);
  const [leads, setLeads] = useState([]);
  
  const [convertData, setConvertData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    time: "09:00",
    duration: 60
  });

  const [newDevis, setNewDevis] = useState({
    lead_id: "",
    client_name: "",
    client_phone: "",
    client_email: "",
    vehicle: "",
    immatriculation: "",
    prestation: "",
    price: 0,
    agency_id: user?.agency_id || "",
    valid_days: 30,
    notes: ""
  });

  useEffect(() => {
    fetchDevis();
    fetchPrestations();
    fetchLeads();
  }, [selectedAgency, selectedStatus]);

  const fetchDevis = async () => {
    setLoading(true);
    try {
      let params = [];
      if (selectedAgency !== "all") params.push(`agency_id=${selectedAgency}`);
      if (selectedStatus !== "all") params.push(`status=${selectedStatus}`);
      
      const queryString = params.length > 0 ? `?${params.join("&")}` : "";
      const res = await axios.get(`${API}/devis${queryString}`);
      setDevisList(res.data);
    } catch (error) {
      console.error("Failed to fetch devis:", error);
      toast.error("Erreur lors du chargement des devis");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrestations = async () => {
    try {
      const res = await axios.get(`${API}/prestations`);
      setPrestations(res.data.prestations);
    } catch (error) {
      console.error("Failed to fetch prestations:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data.filter(l => l.status !== "converti" && l.status !== "perdu"));
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    }
  };

  const handleCreateDevis = async () => {
    try {
      if (!newDevis.client_name || !newDevis.prestation || !newDevis.agency_id) {
        toast.error("Veuillez remplir les champs obligatoires");
        return;
      }
      
      await axios.post(`${API}/devis`, newDevis);
      toast.success("Devis créé avec succès");
      setShowAddDialog(false);
      fetchDevis();
      resetNewDevis();
    } catch (error) {
      toast.error("Erreur lors de la création du devis");
    }
  };

  const handleUpdateStatus = async (devisId, newStatus) => {
    try {
      await axios.put(`${API}/devis/${devisId}`, { status: newStatus });
      toast.success("Statut mis à jour");
      fetchDevis();
      if (selectedDevis?.id === devisId) {
        setSelectedDevis({ ...selectedDevis, status: newStatus });
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleRelance = async (devisId) => {
    try {
      const res = await axios.post(`${API}/devis/${devisId}/relance`);
      toast.success(res.data.message);
      fetchDevis();
    } catch (error) {
      toast.error("Erreur lors de la relance");
    }
  };

  const handleSendEmail = async (devisId) => {
    try {
      const res = await axios.post(`${API}/email/send-devis/${devisId}`);
      if (res.data.success) {
        toast.success(res.data.message);
        fetchDevis();
        if (selectedDevis?.id === devisId) {
          setSelectedDevis({ ...selectedDevis, status: "envoye", email_sent: true });
        }
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi de l'email");
    }
  };

  const handleConvertToAppointment = async () => {
    try {
      await axios.post(`${API}/devis/${selectedDevis.id}/convert-to-appointment`, null, {
        params: { date: convertData.date, time: convertData.time, duration: convertData.duration }
      });
      toast.success("Rendez-vous créé avec succès");
      setShowConvertDialog(false);
      setShowDetailSheet(false);
      fetchDevis();
    } catch (error) {
      toast.error("Erreur lors de la conversion");
    }
  };

  const handleDeleteDevis = async (devisId) => {
    try {
      await axios.delete(`${API}/devis/${devisId}`);
      toast.success("Devis supprimé");
      fetchDevis();
      setShowDetailSheet(false);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLeadSelect = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setNewDevis({
        ...newDevis,
        lead_id: lead.id,
        client_name: lead.name,
        client_phone: lead.phone || "",
        client_email: lead.email || "",
        vehicle: lead.vehicle || "",
        prestation: lead.prestation_souhaitee || "",
        agency_id: lead.agency_id
      });
    }
  };

  const resetNewDevis = () => {
    setNewDevis({
      lead_id: "",
      client_name: "",
      client_phone: "",
      client_email: "",
      vehicle: "",
      immatriculation: "",
      prestation: "",
      price: 0,
      agency_id: user?.agency_id || "",
      valid_days: 30,
      notes: ""
    });
  };

  const filteredDevis = devisList.filter(devis => {
    const query = searchQuery.toLowerCase();
    return (
      devis.client_name?.toLowerCase().includes(query) ||
      devis.devis_number?.toLowerCase().includes(query) ||
      devis.vehicle?.toLowerCase().includes(query)
    );
  });

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency?.city || "—";
  };

  const getDaysRemaining = (validUntil) => {
    if (!validUntil) return null;
    const days = differenceInDays(parseISO(validUntil), new Date());
    return days;
  };

  const TIME_SLOTS = [
    "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
    "11:00", "11:30", "12:00", "14:00", "14:30", "15:00",
    "15:30", "16:00", "16:30", "17:00", "17:30", "18:00"
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl sm:text-3xl font-bold"
            style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}
          >
            DEVIS
          </h1>
          <p className="text-gray-500 mt-1">
            {filteredDevis.length} devis
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button 
                style={{ background: '#FEED00', color: '#000' }}
                data-testid="add-devis-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Devis
              </Button>
            </DialogTrigger>
            <DialogContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }}>
                  NOUVEAU DEVIS
                </DialogTitle>
                <DialogDescription className="text-gray-500">
                  Créer un nouveau devis
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Lead Selection */}
                {leads.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-gray-300">Depuis un lead existant</Label>
                    <Select onValueChange={handleLeadSelect}>
                      <SelectTrigger className="bg-black border-gray-800 text-white">
                        <SelectValue placeholder="Sélectionner un lead..." />
                      </SelectTrigger>
                      <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="max-h-48">
                        {leads.map((lead) => (
                          <SelectItem key={lead.id} value={lead.id} className="text-gray-200">
                            {lead.name} {lead.vehicle && `- ${lead.vehicle}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Nom client *</Label>
                    <Input
                      value={newDevis.client_name}
                      onChange={(e) => setNewDevis({ ...newDevis, client_name: e.target.value })}
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-devis-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Téléphone *</Label>
                    <Input
                      value={newDevis.client_phone}
                      onChange={(e) => setNewDevis({ ...newDevis, client_phone: e.target.value })}
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-devis-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Email</Label>
                  <Input
                    type="email"
                    value={newDevis.client_email}
                    onChange={(e) => setNewDevis({ ...newDevis, client_email: e.target.value })}
                    className="bg-black border-gray-800 text-white"
                    data-testid="new-devis-email"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Véhicule *</Label>
                    <Input
                      value={newDevis.vehicle}
                      onChange={(e) => setNewDevis({ ...newDevis, vehicle: e.target.value })}
                      placeholder="BMW 320d"
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-devis-vehicle"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Immatriculation</Label>
                    <Input
                      value={newDevis.immatriculation}
                      onChange={(e) => setNewDevis({ ...newDevis, immatriculation: e.target.value.toUpperCase() })}
                      placeholder="AB-123-CD"
                      className="bg-black border-gray-800 text-white font-mono"
                      data-testid="new-devis-immat"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Prestation *</Label>
                  <Select 
                    value={newDevis.prestation} 
                    onValueChange={(v) => setNewDevis({ ...newDevis, prestation: v })}
                  >
                    <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="new-devis-prestation">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="max-h-48">
                      {prestations.map((p) => (
                        <SelectItem key={p} value={p} className="text-gray-200">{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300">Prix (€) *</Label>
                    <Input
                      type="number"
                      value={newDevis.price === 0 ? "" : newDevis.price}
                      onChange={(e) => setNewDevis({ ...newDevis, price: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                      placeholder="0"
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-devis-price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300">Validité (jours)</Label>
                    <Input
                      type="number"
                      value={newDevis.valid_days}
                      onChange={(e) => setNewDevis({ ...newDevis, valid_days: parseInt(e.target.value) || 30 })}
                      className="bg-black border-gray-800 text-white"
                      data-testid="new-devis-validity"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Agence *</Label>
                  <Select 
                    value={newDevis.agency_id} 
                    onValueChange={(v) => setNewDevis({ ...newDevis, agency_id: v })}
                  >
                    <SelectTrigger className="bg-black border-gray-800 text-white" data-testid="new-devis-agency">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                      {agencies.map((agency) => (
                        <SelectItem key={agency.id} value={agency.id} className="text-gray-200">
                          {agency.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Notes</Label>
                  <Textarea
                    value={newDevis.notes}
                    onChange={(e) => setNewDevis({ ...newDevis, notes: e.target.value })}
                    placeholder="Notes internes..."
                    className="bg-black border-gray-800 text-white resize-none"
                    rows={2}
                    data-testid="new-devis-notes"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
                  >
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleCreateDevis}
                    className="flex-1"
                    style={{ background: '#FEED00', color: '#000' }}
                    data-testid="confirm-new-devis-btn"
                  >
                    Créer
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
          <Input
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-black border-gray-800 text-white"
            data-testid="search-devis"
          />
        </div>

        {user?.role === "admin" && (
          <Select value={selectedAgency} onValueChange={setSelectedAgency}>
            <SelectTrigger className="w-[140px] bg-black border-gray-800 text-white">
              <Building2 className="w-4 h-4 mr-2 text-gray-600" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
              <SelectItem value="all" className="text-gray-200">Toutes</SelectItem>
              {agencies.map((agency) => (
                <SelectItem key={agency.id} value={agency.id} className="text-gray-200">
                  {agency.city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-[140px] bg-black border-gray-800 text-white">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
            <SelectItem value="all" className="text-gray-200">Tous</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key} className="text-gray-200">
                {config.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Devis Table */}
      <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ borderColor: '#1A1A1A' }} className="hover:bg-transparent">
                <TableHead className="text-gray-500">N° Devis</TableHead>
                <TableHead className="text-gray-500">Client</TableHead>
                <TableHead className="text-gray-500">Prestation</TableHead>
                <TableHead className="text-gray-500 hidden md:table-cell">Prix</TableHead>
                <TableHead className="text-gray-500">Statut</TableHead>
                <TableHead className="text-gray-500 hidden lg:table-cell">Relances</TableHead>
                <TableHead className="text-gray-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i} style={{ borderColor: '#1A1A1A' }}>
                    <TableCell colSpan={7}>
                      <div className="h-12 rounded skeleton" style={{ background: '#1A1A1A' }} />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredDevis.length === 0 ? (
                <TableRow style={{ borderColor: '#1A1A1A' }}>
                  <TableCell colSpan={7} className="text-center py-12 text-gray-600">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun devis trouvé</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDevis.map((devis) => {
                  const statusConfig = STATUS_CONFIG[devis.status] || STATUS_CONFIG.brouillon;
                  const daysRemaining = getDaysRemaining(devis.valid_until);
                  
                  return (
                    <TableRow 
                      key={devis.id} 
                      style={{ borderColor: '#1A1A1A' }}
                      className="table-row-hover cursor-pointer"
                      onClick={() => {
                        setSelectedDevis(devis);
                        setShowDetailSheet(true);
                      }}
                      data-testid={`devis-row-${devis.id}`}
                    >
                      <TableCell>
                        <span className="font-mono text-[#FEED00]">{devis.devis_number}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{devis.client_name}</p>
                          <p className="text-sm text-gray-600">{devis.vehicle}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {devis.prestation}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className="font-bold text-green-400" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                          {devis.price} €
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${statusConfig.color}`} />
                          <span className="text-sm text-gray-300">{statusConfig.label}</span>
                        </div>
                        {daysRemaining !== null && devis.status === "envoye" && (
                          <p className={`text-xs mt-1 ${daysRemaining <= 3 ? 'text-red-400' : 'text-gray-600'}`}>
                            {daysRemaining > 0 ? `${daysRemaining}j restants` : "Expiré"}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {devis.relance_count > 0 && (
                          <Badge variant="outline" style={{ borderColor: '#FEED00', color: '#FEED00' }}>
                            {devis.relance_count} relance{devis.relance_count > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {devis.status === "envoye" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-gray-400 hover:text-[#FEED00]"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRelance(devis.id);
                              }}
                              title="Relancer"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Devis Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="w-full sm:max-w-lg overflow-y-auto">
          {selectedDevis && (
            <>
              <SheetHeader>
                <SheetTitle style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00', letterSpacing: '0.05em' }} className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {selectedDevis.devis_number}
                </SheetTitle>
                <SheetDescription className="text-gray-500">
                  Créé le {format(new Date(selectedDevis.created_at), "dd/MM/yyyy", { locale: fr })}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Client Info */}
                <div className="p-4 rounded-lg space-y-3" style={{ background: '#111' }}>
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-sm text-gray-500">Client</p>
                      <p className="text-white font-medium">{selectedDevis.client_name}</p>
                    </div>
                  </div>
                  {selectedDevis.client_phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-sm text-gray-500">Téléphone</p>
                        <p className="text-white font-mono">{selectedDevis.client_phone}</p>
                      </div>
                    </div>
                  )}
                  {selectedDevis.client_email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-white">{selectedDevis.client_email}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Vehicle & Prestation */}
                <div className="p-4 rounded-lg space-y-3" style={{ background: '#111' }}>
                  <div className="flex items-center gap-3">
                    <Car className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-sm text-gray-500">Véhicule</p>
                      <p className="text-white">{selectedDevis.vehicle}</p>
                      {selectedDevis.immatriculation && (
                        <p className="text-sm text-gray-500 font-mono">{selectedDevis.immatriculation}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Prestation</p>
                      <Badge style={{ background: 'rgba(254, 237, 0, 0.15)', color: '#FEED00' }}>
                        {selectedDevis.prestation}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Prix</p>
                      <p className="text-2xl font-bold text-green-400" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                        {selectedDevis.price} €
                      </p>
                    </div>
                  </div>
                </div>

                {/* Validity */}
                {selectedDevis.valid_until && (
                  <div className="p-4 rounded-lg" style={{ background: '#111' }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">Valide jusqu'au</span>
                      </div>
                      <span className="text-white">{format(parseISO(selectedDevis.valid_until), "dd/MM/yyyy")}</span>
                    </div>
                    {selectedDevis.relance_count > 0 && (
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-gray-500">Relances effectuées</span>
                        <Badge variant="outline" style={{ borderColor: '#FEED00', color: '#FEED00' }}>
                          {selectedDevis.relance_count}
                        </Badge>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                {selectedDevis.notes && (
                  <div className="p-4 rounded-lg" style={{ background: '#111' }}>
                    <p className="text-sm text-gray-500 mb-1">Notes</p>
                    <p className="text-gray-300 text-sm">{selectedDevis.notes}</p>
                  </div>
                )}

                {/* Status Actions */}
                <div className="space-y-2">
                  <p className="text-sm text-gray-500">Statut</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(STATUS_CONFIG).map(([statusKey, config]) => {
                      const Icon = config.icon;
                      const isActive = selectedDevis.status === statusKey;
                      return (
                        <Button
                          key={statusKey}
                          variant={isActive ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleUpdateStatus(selectedDevis.id, statusKey)}
                          className={isActive 
                            ? "bg-[#FEED00] text-black hover:bg-[#FFF44F]" 
                            : "border-gray-700 text-gray-400 hover:border-[#FEED00] hover:text-[#FEED00]"
                          }
                        >
                          <Icon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2 pt-4 border-t" style={{ borderColor: '#1A1A1A' }}>
                  {/* Send by email button */}
                  {(selectedDevis.status === "brouillon" || selectedDevis.status === "envoye") && selectedDevis.client_email && (
                    <Button
                      onClick={() => handleSendEmail(selectedDevis.id)}
                      className="w-full"
                      style={{ background: '#3B82F6', color: '#fff' }}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {selectedDevis.status === "brouillon" ? "Envoyer par email" : "Renvoyer par email"}
                    </Button>
                  )}
                  
                  {(selectedDevis.status === "brouillon" || selectedDevis.status === "envoye") && !selectedDevis.client_email && (
                    <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30 text-center">
                      <p className="text-orange-400 text-sm">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Ajoutez un email au devis pour l'envoyer
                      </p>
                    </div>
                  )}
                  
                  {selectedDevis.status === "envoye" && (
                    <Button
                      onClick={() => handleRelance(selectedDevis.id)}
                      className="w-full"
                      variant="outline"
                      style={{ borderColor: '#FEED00', color: '#FEED00' }}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Marquer comme relancé
                    </Button>
                  )}
                  
                  {(selectedDevis.status === "accepte" || selectedDevis.status === "envoye") && (
                    <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
                      <DialogTrigger asChild>
                        <Button
                          className="w-full"
                          style={{ background: '#FEED00', color: '#000' }}
                        >
                          <ArrowRight className="w-4 h-4 mr-2" />
                          Convertir en RDV
                        </Button>
                      </DialogTrigger>
                      <DialogContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                        <DialogHeader>
                          <DialogTitle style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#FEED00' }}>
                            CRÉER UN RENDEZ-VOUS
                          </DialogTitle>
                          <DialogDescription className="text-gray-500">
                            Choisir la date et l'heure du rendez-vous
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <Label className="text-gray-300">Date</Label>
                            <Input
                              type="date"
                              value={convertData.date}
                              onChange={(e) => setConvertData({ ...convertData, date: e.target.value })}
                              className="bg-black border-gray-800 text-white"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label className="text-gray-300">Heure</Label>
                              <Select 
                                value={convertData.time} 
                                onValueChange={(v) => setConvertData({ ...convertData, time: v })}
                              >
                                <SelectTrigger className="bg-black border-gray-800 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} className="max-h-48">
                                  {TIME_SLOTS.map((time) => (
                                    <SelectItem key={time} value={time} className="text-gray-200">{time}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-300">Durée</Label>
                              <Select 
                                value={convertData.duration.toString()} 
                                onValueChange={(v) => setConvertData({ ...convertData, duration: parseInt(v) })}
                              >
                                <SelectTrigger className="bg-black border-gray-800 text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                                  <SelectItem value="30" className="text-gray-200">30 min</SelectItem>
                                  <SelectItem value="45" className="text-gray-200">45 min</SelectItem>
                                  <SelectItem value="60" className="text-gray-200">1h</SelectItem>
                                  <SelectItem value="90" className="text-gray-200">1h30</SelectItem>
                                  <SelectItem value="120" className="text-gray-200">2h</SelectItem>
                                  <SelectItem value="180" className="text-gray-200">3h</SelectItem>
                                  <SelectItem value="240" className="text-gray-200">4h</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <Button
                            onClick={handleConvertToAppointment}
                            className="w-full"
                            style={{ background: '#FEED00', color: '#000' }}
                          >
                            Créer le RDV
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}

                  <Button
                    variant="ghost"
                    onClick={() => handleDeleteDevis(selectedDevis.id)}
                    className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer le devis
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
